/**
 * @Author: Anandp
 * @Date:   2024-04-17 18:40:49
 * @Last Modified by:   Anandp
 * @Last Modified time: 2024-12-23 15:50:45
 */
import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from 'src/hooks/use-auth';
import { useRouter } from 'src/hooks/use-router';

export const AuthGuard = (props) => {
  const { children } = props;
  const router = useRouter();
  const { isAuthenticated, isInitialized, issuer } = useAuth();
  const [checked, setChecked] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  
  const check = useCallback(() => {
    if (!isInitialized) {
      // Still initializing, don't render yet
      return;
    }
    
    if (!isAuthenticated) {
      const searchParams = new URLSearchParams({ returnTo: window.location.pathname }).toString();
      const href = `/?${searchParams}`;
      router.replace(href);
    } else {
      // Double-check that sessionStorage has the necessary data
      let userDetails = sessionStorage.getItem("userDetails");
      
      // If not in sessionStorage, try to get from localStorage
      if (!userDetails) {
        const localUserDetails = localStorage.getItem("userDetails");
        if (localUserDetails) {
          sessionStorage.setItem("userDetails", localUserDetails);
          userDetails = localUserDetails;
        }
      }
      
      if (!userDetails && retryCount < 10) {
        // Data not ready yet, wait a bit and retry
        setRetryCount(prev => prev + 1);
        setTimeout(() => check(), 100);
        return;
      }
      
      if (!userDetails) {
        // After 10 retries (1 second), clear session and redirect to login
        console.error('Session data not available after initialization');
        sessionStorage.clear();
        window.location.href = '/';
        return;
      }
      
      setChecked(true);
    }
    // eslint-disable-next-line
  }, [isAuthenticated, isInitialized, issuer, router, retryCount]);

  // Check whenever initialization state changes
  useEffect(() => {   
      check();       
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isInitialized, isAuthenticated]);

  if (!checked) {
    return null;
  }

  // If got here, it means that the redirect did not occur, and that tells us that the user is
  // authenticated / authorized.

  return <>{children}</>;
};

AuthGuard.propTypes = {
  children: PropTypes.node
};
